import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  Course,
  Lesson,
  Module,
  Question,
  QuestionOption,
  CourseNavigation,
  UpdateCourseRequest,
  CreateModuleRequest,
  UpdateModuleRequest,
  CreateLessonRequest,
  UpdateLessonRequest,
  CreateQuestionRequest,
  UpdateQuestionRequest,
} from 'core/models/course-content.model';

@Injectable({
  providedIn: 'root',
})
export class CourseEditorService {
  // ==========================================================================
  // PROPRIEDADES
  // ==========================================================================

  private readonly STORAGE_KEY = 'course-editor-data';
  private readonly LESSON_ID_KEY = 'course-editor-current-lesson-id';
  private readonly MODULE_ID_KEY = 'course-editor-current-module-id';
  private readonly QUESTION_ID_KEY = 'course-editor-current-question-id';
  private readonly PREVIEW_MODE_KEY = 'course-editor-preview-mode';
  private readonly INITIALIZED_KEY = 'course-editor-initialized';

  private mockCourse: Course = this.loadCourseFromStorage();
  private currentLessonId: string | null = this.initializeCurrentLessonId();
  private currentModuleId: string | null = this.loadCurrentModuleId();
  private currentQuestionId: string | null = this.loadCurrentQuestionId();
  private isPreviewMode = this.loadPreviewMode();

  private courseSubject = new BehaviorSubject<Course>(this.mockCourse);
  private lessonChangeSubject = new BehaviorSubject<string | null>(
    this.currentLessonId,
  );
  private questionChangeSubject = new BehaviorSubject<string | null>(
    this.currentQuestionId,
  );
  private previewModeSubject = new BehaviorSubject<boolean>(this.isPreviewMode);

  // ==========================================================================
  // CONSTRUCTOR
  // ==========================================================================

  constructor() {
    // Garantir que a primeira lição seja selecionada se ainda não foi selecionada
    // Isso acontece quando os dados mockados são carregados pela primeira vez
    if (!this.currentLessonId && this.mockCourse.modules.length > 0) {
      const firstLessonsModule = this.mockCourse.modules.find(
        (m) => m.contentType === 'lessons' && m.lessons && m.lessons.length > 0,
      );
      if (firstLessonsModule && firstLessonsModule.lessons) {
        this.currentLessonId = firstLessonsModule.lessons[0].id;
        this.saveCurrentLessonId();
        // Atualizar o BehaviorSubject com a primeira lição
        this.lessonChangeSubject.next(this.currentLessonId);
      }
    }
  }

  // ==========================================================================
  // OBSERVABLES PÚBLICOS
  // ==========================================================================

  public course$ = this.courseSubject.asObservable();
  public lessonChange$: Observable<string | null> =
    this.lessonChangeSubject.asObservable();
  public questionChange$: Observable<string | null> =
    this.questionChangeSubject.asObservable();
  public previewMode$ = this.previewModeSubject.asObservable();

  // ==========================================================================
  // MÉTODOS CRUD - READ/LIST
  // ==========================================================================

  /**
   * Obtém o curso completo
   * @returns Curso completo
   */
  public getCourse(): Course {
    return { ...this.mockCourse };
  }

  /**
   * Obtém a lição atual
   * @returns Lição atual ou null
   */
  public getCurrentLesson(): Lesson | null {
    if (!this.currentLessonId) {
      return null;
    }

    for (const module of this.mockCourse.modules) {
      if (module.lessons) {
        const lesson = module.lessons.find(
          (l) => l.id === this.currentLessonId,
        );
        if (lesson) {
          return lesson;
        }
      }
    }
    return null;
  }

  /**
   * Obtém a questão atual
   * @returns Questão atual ou null
   */
  public getCurrentQuestion(): Question | null {
    if (!this.currentQuestionId) {
      return null;
    }

    for (const module of this.mockCourse.modules) {
      if (module.contentType === 'evaluations' && module.questions) {
        const question = module.questions.find(
          (q) => q.id === this.currentQuestionId,
        );
        if (question) {
          return question;
        }
      }
    }
    return null;
  }

  /**
   * Obtém o módulo atual baseado na lição atual ou no módulo selecionado
   * @returns Módulo atual ou null
   */
  public getCurrentModule(): Module | null {
    // Se há uma lição atual, retornar o módulo que contém essa lição
    if (this.currentLessonId) {
      for (const module of this.mockCourse.modules) {
        if (module.lessons) {
          const lesson = module.lessons.find(
            (l) => l.id === this.currentLessonId,
          );
          if (lesson) {
            return module;
          }
        }
      }
    }

    // Se não há lição mas há um módulo selecionado, retornar esse módulo
    if (this.currentModuleId) {
      const module = this.mockCourse.modules.find(
        (m) => m.id === this.currentModuleId,
      );
      if (module) {
        return module;
      }
    }

    return null;
  }

  /**
   * Define o módulo atual (usado quando não há lição selecionada)
   * @param moduleId ID do módulo ou null para limpar
   */
  public setCurrentModule(moduleId: string | null): void {
    this.currentModuleId = moduleId;
    // Limpar lição atual quando definir um módulo diretamente
    // Isso garante que a mensagem de módulo vazio seja exibida
    if (moduleId) {
      this.currentLessonId = null;
      this.saveCurrentLessonId();
      this.lessonChangeSubject.next(null);
    }
    this.saveCurrentModuleId();
  }

  /**
   * Obtém informações de navegação
   * @returns Informações de navegação do curso
   */
  public getNavigation(): CourseNavigation {
    const currentLesson = this.getCurrentLesson();
    const currentModule = this.getCurrentModule();

    // Se não há módulo, retornar navegação vazia
    if (!currentModule) {
      return {
        currentModule: null,
        currentLesson: null,
        currentModuleIndex: -1,
        currentLessonIndex: -1,
        canGoPrevious: false,
        canGoNext: false,
      };
    }

    // Se há módulo mas não há lição, retornar apenas o módulo
    if (!currentLesson) {
      const moduleIndex = this.mockCourse.modules.findIndex(
        (m) => m.id === currentModule.id,
      );
      return {
        currentModule,
        currentLesson: null,
        currentModuleIndex: moduleIndex,
        currentLessonIndex: -1,
        canGoPrevious: false,
        canGoNext: false,
      };
    }

    const moduleIndex = this.mockCourse.modules.findIndex(
      (m) => m.id === currentModule.id,
    );
    const lessonIndex = currentModule.lessons
      ? currentModule.lessons.findIndex((l) => l.id === currentLesson.id)
      : -1;

    const canGoPrevious = moduleIndex > 0 || lessonIndex > 0;
    const canGoNext =
      moduleIndex < this.mockCourse.modules.length - 1 ||
      (currentModule.contentType === 'lessons' &&
        currentModule.lessons &&
        lessonIndex < currentModule.lessons.length - 1);

    return {
      currentModule,
      currentLesson,
      currentModuleIndex: moduleIndex,
      currentLessonIndex: lessonIndex,
      canGoPrevious,
      canGoNext: !!canGoNext,
    };
  }

  // ==========================================================================
  // MÉTODOS CRUD - CREATE
  // ==========================================================================

  /**
   * Cria um novo módulo
   * @param data Dados do módulo a ser criado
   * @returns Módulo criado
   */
  public createModule(data: CreateModuleRequest): Module {
    const newModule: Module = {
      id: `module-${Date.now()}`,
      name: data.name,
      description: data.description,
      contentType: data.contentType,
      order:
        data.order !== undefined ? data.order : this.mockCourse.modules.length,
    };

    // Inicializar estrutura baseada no tipo de conteúdo
    if (data.contentType === 'lessons') {
      newModule.lessons = [];
    } else if (data.contentType === 'evaluations') {
      newModule.questions = [];
    }

    this.mockCourse.modules.push(newModule);
    this.sortModules();
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return { ...newModule };
  }

  /**
   * Cria uma nova lição em um módulo
   * @param moduleId ID do módulo
   * @param data Dados da lição a ser criada
   * @returns Lição criada ou null se módulo não encontrado
   */
  public createLesson(
    moduleId: string,
    data: CreateLessonRequest,
  ): Lesson | null {
    const module = this.mockCourse.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'lessons') {
      return null;
    }

    if (!module.lessons) {
      module.lessons = [];
    }

    const newLesson: Lesson = {
      id: `lesson-${Date.now()}`,
      title: data.title,
      description: data.description,
      type: data.type,
      videoUrl: data.videoUrl,
      pdfUrl: data.pdfUrl,
      duration: data.duration,
      completed: false,
      order: data.order !== undefined ? data.order : module.lessons.length,
    };

    module.lessons.push(newLesson);
    this.sortLessons(module);
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return { ...newLesson };
  }

  /**
   * Cria uma nova questão em um módulo do tipo evaluation
   * @param moduleId ID do módulo
   * @param data Dados da questão a ser criada
   * @returns Questão criada ou null se módulo não encontrado ou tipo incorreto
   */
  public createQuestion(
    moduleId: string,
    data: CreateQuestionRequest,
  ): Question | null {
    const module = this.mockCourse.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'evaluations') {
      return null;
    }

    if (!module.questions) {
      module.questions = [];
    }

    // Gerar IDs para as opções
    const options: QuestionOption[] = data.options.map((opt, index) => ({
      id: `opt-${Date.now()}-${index}`,
      label: opt.label,
      text: opt.text,
    }));

    // O order será definido após adicionar a questão, garantindo sequência correta
    const newQuestion: Question = {
      id: `question-${Date.now()}`,
      text: data.text,
      type: data.type,
      options,
      points: data.points,
      correctAnswers: data.correctAnswers,
      order: data.order !== undefined ? data.order : module.questions.length,
    };

    module.questions.push(newQuestion);
    // Sempre reordenar questões para garantir ordem sequencial começando em 0
    this.sortQuestions(module);
    this.reorderQuestions(module);
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return { ...newQuestion };
  }

  // ==========================================================================
  // MÉTODOS CRUD - UPDATE
  // ==========================================================================

  /**
   * Atualiza informações básicas do curso
   * @param data Dados do curso a serem atualizados
   * @returns Curso atualizado
   */
  public updateCourse(data: UpdateCourseRequest): Course {
    if (data.title !== undefined) {
      this.mockCourse.title = data.title;
    }
    if (data.description !== undefined) {
      this.mockCourse.description = data.description;
    }
    if (data.coverImage !== undefined) {
      this.mockCourse.coverImage = data.coverImage;
    }
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return { ...this.mockCourse };
  }

  /**
   * Atualiza um módulo existente
   * @param moduleId ID do módulo
   * @param data Dados do módulo a serem atualizados
   * @returns Módulo atualizado ou null se não encontrado
   */
  public updateModule(
    moduleId: string,
    data: UpdateModuleRequest,
  ): Module | null {
    const moduleIndex = this.mockCourse.modules.findIndex(
      (m) => m.id === moduleId,
    );
    if (moduleIndex === -1) {
      return null;
    }

    const module = this.mockCourse.modules[moduleIndex];
    if (data.name !== undefined) {
      module.name = data.name;
    }
    if (data.description !== undefined) {
      module.description = data.description;
    }
    if (data.order !== undefined) {
      module.order = data.order;
      this.sortModules();
    }

    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return { ...module };
  }

  /**
   * Atualiza uma lição existente
   * @param moduleId ID do módulo
   * @param lessonId ID da lição
   * @param data Dados da lição a serem atualizados
   * @returns Lição atualizada ou null se não encontrada
   */
  public updateLesson(
    moduleId: string,
    lessonId: string,
    data: UpdateLessonRequest,
  ): Lesson | null {
    const module = this.mockCourse.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'lessons' || !module.lessons) {
      return null;
    }

    const lessonIndex = module.lessons.findIndex((l) => l.id === lessonId);
    if (lessonIndex === -1) {
      return null;
    }

    const lesson = module.lessons[lessonIndex];
    if (data.title !== undefined) {
      lesson.title = data.title;
    }
    if (data.description !== undefined) {
      lesson.description = data.description;
    }
    if (data.type !== undefined) {
      lesson.type = data.type;
    }
    if (data.videoUrl !== undefined) {
      lesson.videoUrl = data.videoUrl;
    }
    if (data.pdfUrl !== undefined) {
      lesson.pdfUrl = data.pdfUrl;
    }
    if (data.duration !== undefined) {
      lesson.duration = data.duration;
    }
    if (data.order !== undefined) {
      lesson.order = data.order;
      this.sortLessons(module);
    }

    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return { ...lesson };
  }

  /**
   * Atualiza uma questão existente em um módulo do tipo evaluation
   * @param moduleId ID do módulo
   * @param questionId ID da questão
   * @param data Dados da questão a serem atualizados
   * @returns Questão atualizada ou null se não encontrada
   */
  public updateQuestion(
    moduleId: string,
    questionId: string,
    data: UpdateQuestionRequest,
  ): Question | null {
    const module = this.mockCourse.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'evaluations' || !module.questions) {
      return null;
    }

    const questionIndex = module.questions.findIndex(
      (q) => q.id === questionId,
    );
    if (questionIndex === -1) {
      return null;
    }

    const question = module.questions[questionIndex];
    if (data.text !== undefined) {
      question.text = data.text;
    }
    if (data.type !== undefined) {
      question.type = data.type;
    }
    if (data.points !== undefined) {
      question.points = data.points;
    }
    if (data.options !== undefined) {
      // Preservar IDs existentes quando possível para manter referências em correctAnswers
      const existingOptionsMapByLabel = new Map(
        question.options.map((opt) => [opt.label, opt.id]),
      );
      const existingOptionsByIndex = question.options;

      question.options = data.options.map((opt, index) => {
        // Primeiro tentar encontrar ID existente pelo label
        let existingId = existingOptionsMapByLabel.get(opt.label);

        // Se não encontrou pelo label e a posição corresponde, tentar pela posição
        if (!existingId && index < existingOptionsByIndex.length) {
          const existingOptionAtPosition = existingOptionsByIndex[index];
          // Só usar o ID da posição se o label não foi usado em outra posição
          if (
            !Array.from(existingOptionsMapByLabel.values()).includes(
              existingOptionAtPosition.id,
            )
          ) {
            existingId = existingOptionAtPosition.id;
          }
        }

        return {
          id: existingId || `opt-${Date.now()}-${index}`,
          label: opt.label,
          text: opt.text,
        };
      });

      // Limpar correctAnswers inválidas (IDs que não existem mais nas opções)
      if (data.correctAnswers === undefined && question.correctAnswers) {
        const validOptionIds = new Set(question.options.map((opt) => opt.id));
        question.correctAnswers = question.correctAnswers.filter((id) =>
          validOptionIds.has(id),
        );
      }
    }
    if (data.correctAnswers !== undefined) {
      question.correctAnswers = data.correctAnswers;
    }
    if (data.order !== undefined) {
      question.order = data.order;
      this.sortQuestions(module);
    }

    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return { ...question };
  }

  // ==========================================================================
  // MÉTODOS CRUD - DELETE
  // ==========================================================================

  /**
   * Exclui um módulo
   * @param moduleId ID do módulo a ser excluído
   * @returns True se excluído com sucesso, false caso contrário
   */
  public deleteModule(moduleId: string): boolean {
    const moduleIndex = this.mockCourse.modules.findIndex(
      (m) => m.id === moduleId,
    );
    if (moduleIndex === -1) {
      return false;
    }

    // Se o módulo atual contém a lição atual, limpar lição atual
    const module = this.mockCourse.modules[moduleIndex];
    if (
      this.currentLessonId &&
      module.lessons &&
      module.lessons.some((l) => l.id === this.currentLessonId)
    ) {
      this.currentLessonId = null;
      this.lessonChangeSubject.next(null);
    }

    this.mockCourse.modules.splice(moduleIndex, 1);
    this.updateModuleOrders();
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return true;
  }

  /**
   * Exclui uma lição
   * @param moduleId ID do módulo
   * @param lessonId ID da lição a ser excluída
   * @returns True se excluída com sucesso, false caso contrário
   */
  public deleteLesson(moduleId: string, lessonId: string): boolean {
    const module = this.mockCourse.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'lessons' || !module.lessons) {
      return false;
    }

    const lessonIndex = module.lessons.findIndex((l) => l.id === lessonId);
    if (lessonIndex === -1) {
      return false;
    }

    // Se é a lição atual, limpar
    if (this.currentLessonId === lessonId) {
      this.currentLessonId = null;
      this.lessonChangeSubject.next(null);
    }

    module.lessons.splice(lessonIndex, 1);
    this.reorderLessons(module);
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return true;
  }

  /**
   * Exclui uma questão de um módulo do tipo evaluation
   * @param moduleId ID do módulo
   * @param questionId ID da questão a ser excluída
   * @returns True se excluída com sucesso, false caso contrário
   */
  public deleteQuestion(moduleId: string, questionId: string): boolean {
    const module = this.mockCourse.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'evaluations' || !module.questions) {
      return false;
    }

    const questionIndex = module.questions.findIndex(
      (q) => q.id === questionId,
    );
    if (questionIndex === -1) {
      return false;
    }

    module.questions.splice(questionIndex, 1);
    this.reorderQuestions(module);
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
    return true;
  }

  // ==========================================================================
  // MÉTODOS AUXILIARES
  // ==========================================================================

  /**
   * Navega para uma lição específica
   * @param lessonId ID da lição ou null para limpar seleção
   */
  public goToLesson(lessonId: string | null): void {
    this.currentLessonId = lessonId;
    // Limpar módulo e questão selecionados quando navegar para uma lição
    this.currentModuleId = null;
    this.currentQuestionId = null;
    this.saveCurrentLessonId();
    this.saveCurrentModuleId();
    this.saveCurrentQuestionId();
    this.lessonChangeSubject.next(lessonId);
    this.questionChangeSubject.next(null);
  }

  /**
   * Navega para uma questão específica
   * @param questionId ID da questão ou null para limpar
   */
  public goToQuestion(questionId: string | null): void {
    this.currentQuestionId = questionId;
    // Limpar lição quando navegar para uma questão
    this.currentLessonId = null;
    // Definir o módulo atual se a questão existir
    if (questionId) {
      for (const module of this.mockCourse.modules) {
        if (module.contentType === 'evaluations' && module.questions) {
          const question = module.questions.find((q) => q.id === questionId);
          if (question) {
            this.currentModuleId = module.id;
            break;
          }
        }
      }
    } else {
      this.currentModuleId = null;
    }
    this.saveCurrentQuestionId();
    this.saveCurrentLessonId();
    this.saveCurrentModuleId();
    this.questionChangeSubject.next(questionId);
    this.lessonChangeSubject.next(null);
  }

  /**
   * Reordena módulos (drag-and-drop)
   * @param newOrder Array com IDs dos módulos na nova ordem
   */
  public reorderModules(newOrder: string[]): void {
    const modulesMap = new Map(this.mockCourse.modules.map((m) => [m.id, m]));
    const reorderedModules: Module[] = [];

    newOrder.forEach((moduleId, index) => {
      const module = modulesMap.get(moduleId);
      if (module) {
        module.order = index;
        reorderedModules.push(module);
      }
    });

    this.mockCourse.modules = reorderedModules;
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
  }

  /**
   * Reordena lições dentro de um módulo (drag-and-drop)
   * @param moduleId ID do módulo
   * @param newOrder Array com IDs das lições na nova ordem
   */
  public reorderLessonsInModule(moduleId: string, newOrder: string[]): void {
    const module = this.mockCourse.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'lessons' || !module.lessons) {
      return;
    }

    const lessonsMap = new Map(module.lessons.map((l) => [l.id, l]));
    const reorderedLessons: Lesson[] = [];

    newOrder.forEach((lessonId, index) => {
      const lesson = lessonsMap.get(lessonId);
      if (lesson) {
        lesson.order = index;
        reorderedLessons.push(lesson);
      }
    });

    module.lessons = reorderedLessons;
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
  }

  /**
   * Reordena questões dentro de um módulo (drag-and-drop)
   * @param moduleId ID do módulo
   * @param newOrder Array com IDs das questões na nova ordem
   */
  public reorderQuestionsInModule(moduleId: string, newOrder: string[]): void {
    const module = this.mockCourse.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'evaluations' || !module.questions) {
      return;
    }

    const questionsMap = new Map(module.questions.map((q) => [q.id, q]));
    const reorderedQuestions: Question[] = [];

    newOrder.forEach((questionId, index) => {
      const question = questionsMap.get(questionId);
      if (question) {
        question.order = index;
        reorderedQuestions.push(question);
      }
    });

    module.questions = reorderedQuestions;
    this.saveCourseToStorage();
    this.courseSubject.next({ ...this.mockCourse });
  }

  /**
   * Ativa/desativa modo preview
   * @param enabled True para ativar, false para desativar
   */
  public setPreviewMode(enabled: boolean): void {
    this.isPreviewMode = enabled;
    this.savePreviewMode();
    this.previewModeSubject.next(enabled);
  }

  /**
   * Verifica se está em modo preview
   * @returns True se está em modo preview, false caso contrário
   */
  public isPreviewModeEnabled(): boolean {
    return this.isPreviewMode;
  }

  /**
   * Calcula o progresso total do curso
   * @returns Porcentagem de progresso (0-100)
   */
  public calculateTotalProgress(): number {
    let totalLessons = 0;
    let completedLessons = 0;

    this.mockCourse.modules.forEach((module) => {
      if (module.contentType === 'lessons' && module.lessons) {
        totalLessons += module.lessons.length;
        completedLessons += module.lessons.filter((l) => l.completed).length;
      }
    });

    return totalLessons > 0
      ? Math.round((completedLessons / totalLessons) * 100)
      : 0;
  }

  /**
   * Calcula o tempo restante estimado
   * @returns String formatada com tempo restante (ex: "~30 min" ou "~1h 30min")
   */
  public calculateRemainingTime(): string {
    let remainingMinutes = 0;

    const navigation = this.getNavigation();
    if (!navigation.currentModule) {
      return '~0 min';
    }

    if (navigation.currentLesson) {
      remainingMinutes += navigation.currentLesson.duration;
    }

    if (
      navigation.currentLessonIndex !== undefined &&
      navigation.currentModule &&
      navigation.currentModule.lessons
    ) {
      const remainingLessonsInModule = navigation.currentModule.lessons.slice(
        navigation.currentLessonIndex + 1,
      );
      remainingLessonsInModule.forEach((lesson) => {
        remainingMinutes += lesson.duration;
      });
    }

    if (navigation.currentModuleIndex !== undefined) {
      const remainingModules = this.mockCourse.modules.slice(
        navigation.currentModuleIndex + 1,
      );
      remainingModules.forEach((module) => {
        if (module.contentType === 'lessons' && module.lessons) {
          module.lessons.forEach((lesson) => {
            remainingMinutes += lesson.duration;
          });
        }
      });
    }

    if (remainingMinutes < 60) {
      return `~${remainingMinutes} min`;
    }

    const hours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;
    return minutes > 0 ? `~${hours}h ${minutes}min` : `~${hours}h`;
  }

  /**
   * Obtém o progresso da lição atual
   * @returns Porcentagem de progresso da lição (no editor, sempre 0)
   */
  public getCurrentLessonProgress(): number {
    return 0; // No editor, sempre 0
  }

  // ==========================================================================
  // MÉTODOS PRIVADOS
  // ==========================================================================

  /**
   * Carrega o curso do localStorage
   * @returns Curso carregado ou curso padrão
   */
  private loadCourseFromStorage(): Course {
    if (typeof window === 'undefined') {
      return this.getDefaultCourse();
    }

    try {
      // Verificar se já foi inicializado com dados mockados
      const isInitialized =
        localStorage.getItem(this.INITIALIZED_KEY) === 'true';

      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Se não foi inicializado ou o curso está vazio, carregar dados mockados
        if (!isInitialized || !parsed.modules || parsed.modules.length === 0) {
          const defaultCourse = this.getDefaultCourse();
          this.saveCourseToStorageInternal(defaultCourse);
          // Marcar como inicializado
          localStorage.setItem(this.INITIALIZED_KEY, 'true');
          return defaultCourse;
        }

        // Normalizar orders de todas as questões para garantir que comecem em 0
        this.normalizeQuestionOrders(parsed);

        return parsed;
      }

      // Se não há dados salvos, carregar dados mockados e salvar automaticamente
      // Isso simula que os cursos foram criados no editor
      const defaultCourse = this.getDefaultCourse();
      this.saveCourseToStorageInternal(defaultCourse);
      // Marcar como inicializado
      localStorage.setItem(this.INITIALIZED_KEY, 'true');
      return defaultCourse;
    } catch (error) {
      console.error('Erro ao carregar curso do localStorage:', error);
      return this.getDefaultCourse();
    }
  }

  /**
   * Salva o curso no localStorage (método interno sem atualizar BehaviorSubject)
   * @param course Curso a ser salvo
   */
  private saveCourseToStorageInternal(course: Course): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(course));
    } catch (error) {
      console.error('Erro ao salvar curso no localStorage:', error);
    }
  }

  /**
   * Retorna o curso padrão com dados mockados do course-viewer
   * Simula que os cursos foram criados no course-editor
   * @returns Curso padrão com dados completos
   */
  private getDefaultCourse(): Course {
    return {
      id: '1',
      title: 'Docker na Prática',
      description:
        'Curso completo sobre Docker com Thiago Veigah - Aprenda a criar e gerenciar containers Docker do zero',
      coverImage: undefined,
      totalProgress: 11,
      modules: [
        {
          id: 'module-1',
          name: 'Introdução ao Docker',
          description:
            'Conceitos fundamentais do Docker e sua importância no desenvolvimento moderno',
          contentType: 'lessons',
          order: 1,
          lessons: [
            {
              id: 'lesson-1-1',
              title: 'O que é o Docker e para que serve?',
              description:
                'Entenda os conceitos fundamentais do Docker, containers e como essa tecnologia revoluciona o desenvolvimento de software.',
              type: 'video',
              videoUrl: 'https://www.youtube.com/embed/4Z-raAFlHf4',
              duration: 15,
              completed: true,
              order: 1,
            },
            {
              id: 'lesson-1-2',
              title: 'Guia Completo: Conceitos de Containers',
              description:
                'Documentação detalhada sobre containers, imagens e a diferença entre Docker e virtualização tradicional.',
              type: 'pdf',
              pdfUrl: 'https://pdfobject.com/pdf/sample.pdf',
              duration: 20,
              completed: false,
              order: 2,
            },
            {
              id: 'lesson-1-3',
              title: 'História e Evolução do Docker',
              description:
                'Conheça a história do Docker, desde sua criação até os dias atuais, e entenda seu impacto na indústria.',
              type: 'video',
              videoUrl: 'https://www.youtube.com/embed/4Z-raAFlHf4',
              duration: 12,
              completed: false,
              order: 3,
            },
          ],
        },
        {
          id: 'module-2',
          name: 'Instalação e Configuração',
          description:
            'Aprenda a instalar e configurar o Docker em diferentes sistemas operacionais',
          contentType: 'lessons',
          order: 2,
          lessons: [
            {
              id: 'lesson-2-1',
              title: 'Como instalar o Docker no MacOS, Linux e Windows',
              description:
                'Passo a passo completo para instalar o Docker em diferentes plataformas e sistemas operacionais.',
              type: 'video',
              videoUrl: 'https://www.youtube.com/embed/4Z-raAFlHf4',
              duration: 18,
              completed: false,
              order: 1,
            },
            {
              id: 'lesson-2-2',
              title: 'Configuração Inicial e Primeiros Passos',
              description:
                'Aprenda a configurar o Docker após a instalação e execute seu primeiro container.',
              type: 'video',
              videoUrl: 'https://www.youtube.com/embed/4Z-raAFlHf4',
              duration: 14,
              completed: false,
              order: 2,
            },
            {
              id: 'lesson-2-3',
              title: 'Guia de Referência Rápida do Docker',
              description:
                'Documentação completa com comandos essenciais e boas práticas para uso do Docker.',
              type: 'pdf',
              pdfUrl: 'https://pdfobject.com/pdf/sample.pdf',
              duration: 25,
              completed: false,
              order: 3,
            },
            {
              id: 'lesson-2-4',
              title: 'Troubleshooting: Resolvendo Problemas Comuns',
              description:
                'Guia prático para resolver os problemas mais comuns durante a instalação e configuração do Docker.',
              type: 'pdf',
              pdfUrl: 'https://pdfobject.com/pdf/sample.pdf',
              duration: 12,
              completed: false,
              order: 4,
            },
          ],
        },
        {
          id: 'module-3',
          name: 'Containers e Imagens',
          description:
            'Domine a criação e gerenciamento de containers e imagens Docker',
          contentType: 'lessons',
          order: 3,
          lessons: [
            {
              id: 'lesson-3-1',
              title:
                'Criando uma imagem e um container Docker para uma aplicação',
              description:
                'Aprenda a criar sua primeira imagem Docker e executar containers para aplicações reais.',
              type: 'video',
              videoUrl: 'https://www.youtube.com/embed/4Z-raAFlHf4',
              duration: 22,
              completed: false,
              order: 1,
            },
            {
              id: 'lesson-3-2',
              title: 'Dockerfile: Guia Completo de Boas Práticas',
              description:
                'Documentação detalhada sobre como escrever Dockerfiles eficientes e seguir as melhores práticas da indústria.',
              type: 'pdf',
              pdfUrl: 'https://pdfobject.com/pdf/sample.pdf',
              duration: 30,
              completed: false,
              order: 2,
            },
            {
              id: 'lesson-3-3',
              title:
                'Como criar um Container de MongoDB e conectá-lo a uma aplicação',
              description:
                'Configure e gerencie containers de banco de dados MongoDB e conecte-os às suas aplicações.',
              type: 'video',
              videoUrl: 'https://www.youtube.com/embed/4Z-raAFlHf4',
              duration: 20,
              completed: false,
              order: 3,
            },
            {
              id: 'lesson-3-4',
              title: 'Como criar uma imagem Docker de uma SPA em React',
              description:
                'Containerize uma Single Page Application (SPA) React e prepare-a para produção.',
              type: 'video',
              videoUrl: 'https://www.youtube.com/embed/4Z-raAFlHf4',
              duration: 18,
              completed: false,
              order: 4,
            },
            {
              id: 'lesson-3-5',
              title: 'Gerenciamento de Imagens: Tags, Versões e Registries',
              description:
                'Aprenda a gerenciar versões de imagens, trabalhar com tags e usar registries públicos e privados.',
              type: 'pdf',
              pdfUrl: 'https://pdfobject.com/pdf/sample.pdf',
              duration: 16,
              completed: false,
              order: 5,
            },
          ],
        },
        {
          id: 'module-4',
          name: 'NGINX e Servindo Aplicações',
          description:
            'Aprenda a usar NGINX para servir aplicações em containers Docker',
          contentType: 'lessons',
          order: 4,
          lessons: [
            {
              id: 'lesson-4-1',
              title: 'O que é NGINX e para que serve?',
              description:
                'Conheça o NGINX, um servidor web de alto desempenho e como utilizá-lo em containers Docker.',
              type: 'video',
              videoUrl: 'https://www.youtube.com/embed/4Z-raAFlHf4',
              duration: 16,
              completed: false,
              order: 1,
            },
            {
              id: 'lesson-4-2',
              title: 'Configuração de NGINX: Guia de Referência',
              description:
                'Documentação completa sobre configuração de NGINX, incluindo exemplos práticos e casos de uso comuns.',
              type: 'pdf',
              pdfUrl: 'https://pdfobject.com/pdf/sample.pdf',
              duration: 22,
              completed: false,
              order: 2,
            },
            {
              id: 'lesson-4-3',
              title: 'Criando um container para o NGINX servir minha SPA',
              description:
                'Configure um container NGINX para servir sua Single Page Application de forma eficiente.',
              type: 'video',
              videoUrl: 'https://www.youtube.com/embed/4Z-raAFlHf4',
              duration: 17,
              completed: false,
              order: 3,
            },
          ],
        },
        {
          id: 'module-5',
          name: 'Avaliação Final',
          description:
            'Teste seus conhecimentos com diferentes tipos de questões sobre Docker',
          contentType: 'evaluations',
          order: 5,
          questions: [
            {
              id: 'q1',
              text: 'O que é um container Docker?',
              type: 'single-choice',
              points: 10,
              order: 0,
              correctAnswers: ['opt2'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'Uma máquina virtual completa com sistema operacional próprio',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'Um pacote isolado contendo uma aplicação e suas dependências',
                },
                {
                  id: 'opt3',
                  label: 'c',
                  text: 'Um serviço de nuvem para hospedar aplicações',
                },
                {
                  id: 'opt4',
                  label: 'd',
                  text: 'Uma linguagem de programação para desenvolvimento web',
                },
              ],
            },
            {
              id: 'q2',
              text: 'Qual comando Docker é usado para criar e iniciar um container a partir de uma imagem?',
              type: 'single-choice',
              points: 10,
              order: 1,
              correctAnswers: ['opt1'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'docker run',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'docker start',
                },
                {
                  id: 'opt3',
                  label: 'c',
                  text: 'docker create',
                },
                {
                  id: 'opt4',
                  label: 'd',
                  text: 'docker build',
                },
              ],
            },
            {
              id: 'q3',
              text: 'Quais são as principais vantagens do Docker?',
              type: 'multiple-choice',
              points: 15,
              order: 2,
              correctAnswers: ['opt1', 'opt2', 'opt4', 'opt5'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'Isolamento de aplicações',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'Portabilidade entre ambientes',
                },
                {
                  id: 'opt3',
                  label: 'c',
                  text: 'Aumento do consumo de recursos',
                },
                {
                  id: 'opt4',
                  label: 'd',
                  text: 'Facilidade de escalabilidade',
                },
                {
                  id: 'opt5',
                  label: 'e',
                  text: 'Ambiente de desenvolvimento consistente',
                },
              ],
            },
            {
              id: 'q4',
              text: 'Quais comandos Docker são usados para gerenciar containers?',
              type: 'multiple-choice',
              points: 12,
              order: 3,
              correctAnswers: ['opt1', 'opt3', 'opt4'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'docker run',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'docker delete',
                },
                {
                  id: 'opt3',
                  label: 'c',
                  text: 'docker ps',
                },
                {
                  id: 'opt4',
                  label: 'd',
                  text: 'docker stop',
                },
                {
                  id: 'opt5',
                  label: 'e',
                  text: 'docker remove',
                },
              ],
            },
            {
              id: 'q5',
              text: 'Docker é uma plataforma de virtualização de máquinas completas.',
              type: 'boolean',
              points: 5,
              order: 4,
              correctAnswers: ['opt2'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'Verdadeiro',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'Falso',
                },
              ],
            },
            {
              id: 'q6',
              text: 'Docker Compose permite orquestrar múltiplos containers através de um arquivo YAML.',
              type: 'boolean',
              points: 5,
              order: 5,
              correctAnswers: ['opt1'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'Verdadeiro',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'Falso',
                },
              ],
            },
            {
              id: 'q7',
              text: 'Uma imagem Docker é uma instância em execução de um container.',
              type: 'boolean',
              points: 5,
              order: 6,
              correctAnswers: ['opt2'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'Verdadeiro',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'Falso',
                },
              ],
            },
            {
              id: 'q8',
              text: 'O Dockerfile é usado para definir como construir uma imagem Docker.',
              type: 'boolean',
              points: 5,
              order: 7,
              correctAnswers: ['opt1'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'Verdadeiro',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'Falso',
                },
              ],
            },
            {
              id: 'q9',
              text: 'Qual é a diferença entre uma imagem e um container Docker?',
              type: 'single-choice',
              points: 10,
              order: 8,
              correctAnswers: ['opt3'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'Não há diferença, são a mesma coisa',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'Imagem é o código-fonte e container é o executável',
                },
                {
                  id: 'opt3',
                  label: 'c',
                  text: 'Imagem é um template e container é uma instância em execução da imagem',
                },
                {
                  id: 'opt4',
                  label: 'd',
                  text: 'Imagem é para desenvolvimento e container é para produção',
                },
              ],
            },
            {
              id: 'q10',
              text: 'Quais recursos do Docker ajudam na portabilidade de aplicações?',
              type: 'multiple-choice',
              points: 12,
              order: 9,
              correctAnswers: ['opt1', 'opt2', 'opt3'],
              options: [
                {
                  id: 'opt1',
                  label: 'a',
                  text: 'Isolamento de dependências',
                },
                {
                  id: 'opt2',
                  label: 'b',
                  text: 'Ambiente consistente entre desenvolvimento e produção',
                },
                {
                  id: 'opt3',
                  label: 'c',
                  text: 'Compatibilidade multiplataforma',
                },
                {
                  id: 'opt4',
                  label: 'd',
                  text: 'Requer configuração manual em cada ambiente',
                },
              ],
            },
          ],
        },
      ],
    };
  }

  /**
   * Salva o curso no localStorage
   */
  private saveCourseToStorage(): void {
    this.saveCourseToStorageInternal(this.mockCourse);
  }

  /**
   * Inicializa a lição atual, carregando do localStorage ou selecionando a primeira
   * @returns ID da lição atual ou null
   */
  private initializeCurrentLessonId(): string | null {
    const loadedId = this.loadCurrentLessonId();

    // Se não há lição salva e há módulos/lições disponíveis, selecionar a primeira
    // Isso simula o comportamento do course-viewer onde a primeira lição é carregada automaticamente
    if (!loadedId) {
      // Verificar se o curso foi carregado (pode estar vazio ainda na inicialização)
      const course = this.mockCourse;
      if (course && course.modules && course.modules.length > 0) {
        // Buscar o primeiro módulo do tipo 'lessons' que tenha lições
        const firstLessonsModule = course.modules.find(
          (m) =>
            m.contentType === 'lessons' && m.lessons && m.lessons.length > 0,
        );
        if (firstLessonsModule && firstLessonsModule.lessons) {
          const firstLessonId = firstLessonsModule.lessons[0].id;
          // Salvar automaticamente a primeira lição selecionada
          if (typeof window !== 'undefined') {
            try {
              localStorage.setItem(this.LESSON_ID_KEY, firstLessonId);
            } catch (error) {
              console.error(
                'Erro ao salvar primeira lição no localStorage:',
                error,
              );
            }
          }
          return firstLessonId;
        }
      }
    }

    return loadedId;
  }

  /**
   * Carrega a lição atual do localStorage
   * @returns ID da lição atual ou null
   */
  private loadCurrentLessonId(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return localStorage.getItem(this.LESSON_ID_KEY);
    } catch (error) {
      console.error('Erro ao carregar lição atual do localStorage:', error);
      return null;
    }
  }

  /**
   * Salva a lição atual no localStorage
   */
  private saveCurrentLessonId(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (this.currentLessonId) {
        localStorage.setItem(this.LESSON_ID_KEY, this.currentLessonId);
      } else {
        localStorage.removeItem(this.LESSON_ID_KEY);
      }
    } catch (error) {
      console.error('Erro ao salvar lição atual do localStorage:', error);
    }
  }

  /**
   * Carrega o modo preview do localStorage
   * @returns True se modo preview está ativo, false caso contrário
   */
  private loadPreviewMode(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const stored = localStorage.getItem(this.PREVIEW_MODE_KEY);
      return stored === 'true';
    } catch (error) {
      console.error('Erro ao carregar modo preview do localStorage:', error);
      return false;
    }
  }

  /**
   * Salva o modo preview no localStorage
   */
  private savePreviewMode(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(this.PREVIEW_MODE_KEY, String(this.isPreviewMode));
    } catch (error) {
      console.error('Erro ao salvar modo preview no localStorage:', error);
    }
  }

  /**
   * Atualiza a ordem dos módulos após exclusão
   */
  private updateModuleOrders(): void {
    this.mockCourse.modules.forEach((module, index) => {
      module.order = index;
    });
  }

  /**
   * Ordena módulos por ordem
   */
  private sortModules(): void {
    this.mockCourse.modules.sort((a, b) => a.order - b.order);
  }

  /**
   * Reordena lições após exclusão
   * @param module Módulo a ter lições reordenadas
   */
  private reorderLessons(module: Module): void {
    if (module.lessons) {
      module.lessons.forEach((lesson, index) => {
        lesson.order = index;
      });
    }
  }

  /**
   * Ordena lições por ordem
   * @param module Módulo a ter lições ordenadas
   */
  private sortLessons(module: Module): void {
    if (module.lessons) {
      module.lessons.sort((a, b) => a.order - b.order);
    }
  }

  /**
   * Normaliza os orders de todas as questões em todos os módulos do curso
   * Garante que todas as questões comecem com order = 0
   * @param course Curso a ter questões normalizadas
   */
  private normalizeQuestionOrders(course: Course): void {
    course.modules.forEach((module) => {
      if (
        module.contentType === 'evaluations' &&
        module.questions &&
        module.questions.length > 0
      ) {
        // Ordenar questões pelo order atual
        module.questions.sort((a, b) => a.order - b.order);
        // Reordenar começando em 0
        module.questions.forEach((question, index) => {
          question.order = index;
        });
      }
    });
  }

  /**
   * Reordena questões após exclusão
   * @param module Módulo a ter questões reordenadas
   */
  private reorderQuestions(module: Module): void {
    if (module.questions) {
      module.questions.forEach((question, index) => {
        question.order = index;
      });
    }
  }

  /**
   * Ordena questões por ordem
   * @param module Módulo a ter questões ordenadas
   */
  private sortQuestions(module: Module): void {
    if (module.questions) {
      module.questions.sort((a, b) => a.order - b.order);
    }
  }

  /**
   * Carrega o ID do módulo atual do localStorage
   * @returns ID do módulo atual ou null
   */
  private loadCurrentModuleId(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const stored = localStorage.getItem(this.MODULE_ID_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Erro ao carregar módulo atual do localStorage:', error);
      return null;
    }
  }

  /**
   * Salva o ID do módulo atual no localStorage
   */
  private saveCurrentModuleId(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (this.currentModuleId) {
        localStorage.setItem(
          this.MODULE_ID_KEY,
          JSON.stringify(this.currentModuleId),
        );
      } else {
        localStorage.removeItem(this.MODULE_ID_KEY);
      }
    } catch (error) {
      console.error('Erro ao salvar módulo atual no localStorage:', error);
    }
  }

  /**
   * Carrega a questão atual do localStorage
   * @returns ID da questão atual ou null
   */
  private loadCurrentQuestionId(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return localStorage.getItem(this.QUESTION_ID_KEY);
    } catch (error) {
      console.error('Erro ao carregar questão atual do localStorage:', error);
      return null;
    }
  }

  /**
   * Salva a questão atual no localStorage
   */
  private saveCurrentQuestionId(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (this.currentQuestionId) {
        localStorage.setItem(this.QUESTION_ID_KEY, this.currentQuestionId);
      } else {
        localStorage.removeItem(this.QUESTION_ID_KEY);
      }
    } catch (error) {
      console.error('Erro ao salvar questão atual do localStorage:', error);
    }
  }
}
